import type { RootState } from 'src/redux/store';

import { toast } from 'sonner';
import { useParams } from 'react-router';
import React, { useState, useEffect, useCallback } from 'react';
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { removeTenantIdLS } from 'src/utils/localStorageUtils';

import { store } from 'src/redux/store';
import { useTranslate } from 'src/locales';
import { RECAPTCHA_SITE_KEY } from 'src/config';
import { useAppDispatch, useAppSelector } from 'src/hook/hooks';
import { setChatStateInitial } from 'src/pages/chat/slice/chatSlice';
import { resetSettingState } from 'src/pages/setting/slice/settingSlice';
import { setChannelStateInitial } from 'src/pages/channel/slice/channelSlice';
import { getPostLoginRedirectPath } from 'src/permissions/post-login-redirect';
import { useCallContext } from 'src/pages/chat/channel/call/hooks/use-call-context';

import { AnimateLogoRotate } from 'src/components/animate';

import { useAuthContext } from 'src/auth/hooks';
import { signOut, setSession } from 'src/auth/context/jwt';

import { AuthLoader } from '../shared/AuthLoader';
import { setAuthStateInitial } from '../../slice/authSlice';
import { useLoginErrorHandler } from './hooks/use-login-error-handler';
import { useLogoutUserMutation, useAutoLoginUserMutation } from '../../slice/authApi';

const AutoLogin = () => {
  const { token, email } = useParams<{ token?: string; email?: string }>();
  const [autoLoginUser, { isLoading }] = useAutoLoginUserMutation();
  const [logoutUser, { data: logoutApiResp }] = useLogoutUserMutation();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const dispatch = useAppDispatch();
  const router = useRouter();
  const { t } = useTranslate('translation');
  const { loginApiData } = useAppSelector((state: RootState) => state.auth);
  const { handleResetCallDetails } = useCallContext();
  const { checkUserSession } = useAuthContext();

  const [isRecaptchaReady, setRecaptchaReady] = useState(false);
  const [canAttemptLogin, setCanAttemptLogin] = useState(false);
  const [isLoaderLogin, setIsLoaderLogin] = useState(false);

  const { handleLoginError } = useLoginErrorHandler();

  // Wait for recaptcha to be ready
  useEffect(() => {
    if (executeRecaptcha) {
      setRecaptchaReady(true);
    }
  }, [executeRecaptcha]);

  // Trigger login once recaptcha and params are ready
  useEffect(() => {
    const loginWithToken = async () => {
      try {
        if (!token || !email || !executeRecaptcha) {
          router.replace(paths.auth.signIn);
          return;
        }

        // Logout current session if any
        if (
          loginApiData &&
          loginApiData.email &&
          'token' in loginApiData &&
          typeof loginApiData.token === 'string'
        ) {
          await logoutUser({ email: loginApiData?.email }).unwrap();
        }

        const captchaToken = await executeRecaptcha('login');

        if (!captchaToken) {
          toast.error(t('CAPTCHA_VERIFICATION_FAILED'));
          return;
        }

        const response = await autoLoginUser({
          email,
          imPersonateToken: token,
          captchaToken,
        }).unwrap();

        if (response.success) {
          const accessToken = response?.data?.user?.token;
          if (!accessToken) throw new Error('Access token missing from response.');

          setSession(accessToken);
          await checkUserSession?.();
          const login = store.getState().auth.loginApiData;
          router.replace(getPostLoginRedirectPath(login));
        } else {
          console.error('Auto-login failed:', response.message || 'Unknown error');
          router.replace(paths.auth.signIn);
        }

        setIsLoaderLogin(false);
      } catch (error: any) {
        handleLoginError(error, { shouldRedirect: true });
        setIsLoaderLogin(false);
      }
    };

    if (token && email && isRecaptchaReady && !canAttemptLogin) {
      setCanAttemptLogin(true);
      loginWithToken();
      setIsLoaderLogin(true);
    }
  }, [token, email, isRecaptchaReady]);

  // Clear state after logout
  const clearState = useCallback(async () => {
    await signOut(); // Reset Session
    dispatch(setAuthStateInitial());
    dispatch(setChatStateInitial());
    dispatch(setChannelStateInitial());
    dispatch(resetSettingState());
    handleResetCallDetails();
    removeTenantIdLS();
    await checkUserSession?.();
  }, []);

  useEffect(() => {
    if (logoutApiResp?.success && logoutApiResp.message === 'Logout successful') {
      clearState();
    }
  }, [logoutApiResp]);

  return (
    <div style={{ textAlign: 'center' }}>
      <AnimateLogoRotate sx={{ mb: 3, mx: 'auto' }} />
      {(isLoading || isLoaderLogin || !isRecaptchaReady) && <AuthLoader />}
      <h2>{`${t('PLEASE_WAIT')} ${t('LOGGING_YOU_IN')}`}</h2>
    </div>
  );
};

export function AutoLoginView() {
  const { currentLang } = useTranslate('translation');
  const siteKey = RECAPTCHA_SITE_KEY;

  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey} language={currentLang.value || 'en'}>
      <AutoLogin />
    </GoogleReCaptchaProvider>
  );
}
